// Frontend/src/lib/notificationSound.ts
//
// A two-note chime for "a notification just arrived", synthesised with the
// Web Audio API rather than shipped as an asset file — no license question,
// no extra network request, and no format-support guessing across browsers.
//
// Browsers block audio until the page has seen a user gesture (a click, a
// keypress). By the time a notification can arrive, the resident has almost
// always logged in and clicked something, but this must never throw on the
// rare page that hasn't: every failure is swallowed, because a missed chime
// is nothing and an uncaught exception here would break the poll loop that
// calls it.

let sharedContext: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedContext) sharedContext = new Ctor();
  return sharedContext;
}

function tone(ctx: AudioContext, frequency: number, startAt: number, duration: number) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = frequency;

  // Fade in/out rather than a hard on/off — a square edge on a sine wave is
  // an audible click, not a chime.
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(0.15, startAt + 0.015);
  gain.gain.linearRampToValueAtTime(0, startAt + duration);

  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration);
}

export function playNotificationChime(): void {
  try {
    const ctx = getContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      // Fires without awaiting: if the browser refuses (no gesture yet),
      // this rejects and the catch below silences it.
      void ctx.resume();
    }
    const now = ctx.currentTime;
    tone(ctx, 880, now, 0.12); // A5
    tone(ctx, 1318.5, now + 0.1, 0.18); // E6 — a rising two-note chime
  } catch {
    // Autoplay policy, no AudioContext, a suspended context that refused to
    // resume — none of it should ever be visible to the user.
  }
}
